'use client';
import React, { useState } from 'react';
import { enTeteCollant } from '@/shared/Tableau';
import { useGed } from '@/lib/store';
import { personnelReel } from '@/lib/comptes';
import { card, RefISO, FilterBar, EmptyState, inputStyle, Selecteur, Skeleton, enChargement } from '@/components/ui';
import { H1 } from './commun';
import { Norme } from '../../Norme';

/** Les trois états d'une case — fermés, parce que `ETIQ` les indexe. Une
 *  valeur libre y glisserait sans erreur et s'afficherait en blanc. */
type EtatCase = 'habilite' | 'formation';

/**
 * MATRICE D'HABILITATIONS — par POSTE DE TRAVAIL (ISO 15189 §6.2).
 *
 * ── Ce que cet écran montrait, et pourquoi il paraissait inutile ───────────
 *
 * Il présentait des colonnes de DISCIPLINES et de PROCESSUS. Or la matrice
 * stockée (`ged-setting.habilitations`) ne contient, en base, que des CODES DE
 * POSTE — ACCUEIL1, CHIMIE, TRI, RAMADAN… : 23 axes, aucune discipline. Les
 * colonnes affichées ne correspondaient donc à AUCUNE clé des données.
 *
 * Conséquence, constatée le 19/08/2026 : l'écran montrait une grille
 * intégralement vide, les 23 habilitations réelles étaient invisibles et non
 * modifiables, et cocher une case y écrivait une entrée sous un nom de
 * discipline que plus aucun verrou ne lit. « J'ai l'impression que ce bouton ne
 * sert plus à rien » : il ne servait pas à rien, il servait à écrire à côté.
 *
 * ── Pourquoi le POSTE, et non la discipline ───────────────────────────────
 *
 * Décision de la RQ, déjà appliquée au MOTEUR le 17/08/2026
 * (`controleHabilitation`) : on habilite quelqu'un à tenir un POSTE. Le poste
 * est ce qu'on occupe un jour donné, ce que le planning affecte, et ce que la
 * grille d'habilitation décrit. La discipline, elle, ne dit pas si l'on sait
 * tenir l'accueil un samedi matin.
 *
 * Le serveur lisait déjà par poste ; seul cet écran était resté en arrière.
 *
 * ── Ce que l'écran NE fait pas ────────────────────────────────────────────
 *
 * Il n'évalue personne. Cocher « habilité » est le CONSTAT d'une décision prise
 * ailleurs — au terme d'un dossier de grille, dans « Habilitations &
 * compétences ». Retirer une habilitation, en revanche, retire un droit
 * immédiatement : cela demande confirmation.
 */
export function Habilitations() {
  const { settings, rhPostes, rhDetenues, naviguerVers, loading, booted } = useGed();
  /* BLOQ-03 : la matrice croise TROIS sources — l'effectif (`settings`), les
     postes et les habilitations détenues. Tant que l'une manque, « Aucun
     opérateur ne correspond » est faux, et la phrase d'indice va jusqu'à
     affirmer « personne n'est dépourvu d'habilitation — c'est le résultat
     attendu ». Rassurer à tort sur un écran de compétences est le pire des
     faux vides. */
  const attend = enChargement(booted, loading, settings?.gedUsers?.length || 0, rhPostes.length, rhDetenues.length);
  const tousUsers = personnelReel(settings?.gedUsers || []);
  /* ── LA MATRICE EST DÉRIVÉE, PLUS SAISIE (lot D, 27/08/2026) ─────────────
   *
   * L'écran lisait `settings.habilitations`, un JSON de réglage posé à la main.
   * Deux défauts l'ont condamné : six de ses vingt-deux axes étaient PÉRIMÉS
   * depuis le renommage des paillasses du 25/08, et le verrou de signature,
   * qui y cherchait le processus d'un document, n'y trouvait jamais rien —
   * il REFUSAIT donc tout le monde sur les 34 documents.
   *
   * La matrice se calcule maintenant : une personne « tient » un poste quand
   * elle détient toutes les habilitations qu'il exige. Même forme, vraie
   * source.
   *
   * ⚠️ Elle n'est plus dans `settings` : on la reconstitue ici depuis
   * `rhPostes` et `rhDetenues`, que l'écran charge déjà pour la colonne
   * « déduit ». Les deux lectures donnaient auparavant des résultats
   * différents sur la même ligne — c'est cet écart-là qui disparaît.
   *
   * ⚠️ Un poste SANS exigence n'habilite personne : l'entretien et le coursier
   * n'exigent rien, et les afficher habilités pour tout l'effectif remplirait
   * la grille de cases qui ne prouvent rien. */
  /* ⚠️ La forme est TYPÉE explicitement. Auparavant `settings.habilitations`
   arrivait typé, et `ETIQ[v]` compilait ; une valeur libre y aurait glissé
   sans erreur. On garde la contrainte plutôt que de la desserrer. */
  const hab = React.useMemo<Record<string, Record<string, EtatCase>>>(() => {
    const out: Record<string, Record<string, EtatCase>> = {};
    for (const d of rhDetenues || []) {
      const cle = String(d.orgId || '');
      if (!cle) continue;
      if (!out[cle]) out[cle] = {};
    }
    for (const [orgId, cases] of Object.entries(out)) {
      const siennes = new Map((rhDetenues || [])
        .filter((d) => String(d.orgId) === orgId)
        .map((d) => [String(d.code), String(d.niveau || '')]));
      for (const p of rhPostes) {
        if (p.actif === false) continue;
        const exige = (p.habilitationsRequises || [])
          .map((e) => (typeof e === 'string' ? e : String(e?.code || ''))).filter(Boolean);
        if (!exige.length) continue;
        const tenues = exige.filter((c) => siennes.has(c));
        if (!tenues.length) continue;
        cases[p.code] = tenues.length < exige.length
          || tenues.some((c) => siennes.get(c) === 'formation') ? 'formation' : 'habilite';
      }
    }
    return out;
  }, [rhDetenues, rhPostes]);

  /* LES COLONNES SONT LES POSTES. Deux sources réunies, et l'ordre compte :
   *  · les postes ACTIFS du référentiel, d'abord — ce sont ceux qu'on ouvre ;
   *  · puis les axes présents dans la matrice qui ne correspondent à aucun
   *    poste actif. Ceux-là sont des HÉRITAGES (un poste retiré du service, un
   *    axe d'avant la bascule). Les masquer ferait disparaître de l'écran des
   *    habilitations qui restent en base et restent opposables en audit — le
   *    défaut même que cette refonte corrige. Ils sont donc affichés, et
   *    signalés comme tels. */
  const codesPostes = rhPostes.filter((p) => p.actif !== false).map((p) => p.code);
  const axesEnBase = [...new Set(Object.values(hab).flatMap((m) => Object.keys((m || {}) as object)))];
  const heritages = axesEnBase.filter((a) => !codesPostes.includes(a)).sort();
  const toutesDiscs = [...codesPostes, ...heritages];
  const estProcessus = (nom: string) => heritages.includes(nom);
  const libellePoste = (code: string) => rhPostes.find((p) => p.code === code)?.libelle || code;

  /* ── CE QUE LE DOSSIER DE CHACUN DIT DÉJÀ ──────────────────────────────────
   *
   * « Je voudrais que les cases soient extraites du dossier de la personne :
   * au fur et à mesure des habilitations, en formation ou habilité » (RQ,
   * 19/08/2026). C'est possible, et la chaîne existe déjà en entier :
   *
   *     le POSTE exige des habilitations   (rh-poste.habilitationsRequises)
   *     la PERSONNE en détient              (ged-habilitation-personne — 235 lignes)
   *     ⇒ elle tient le poste, ou elle est en cours
   *
   * ⚠️ La déduction est AFFICHÉE À CÔTÉ de la valeur saisie, elle ne la
   * remplace pas. Deux raisons, et la seconde est la plus forte :
   *  · 87 cases ont été posées à la main et engagent le laboratoire ; les
   *    écraser par un calcul, même juste, effacerait des décisions ;
   *  · un poste qui n'exige AUCUNE habilitation (accueil, entretien) ne peut
   *    rien déduire — et « rien à déduire » ne veut pas dire « non habilité ».
   *    Confondre les deux fermerait ces postes à tout le monde.
   *
   * Le jour où l'écart est partout nul, la déduction pourra devenir la source.
   * Tant qu'il ne l'est pas, c'est l'écart qui est l'information. */
  const detenues = React.useMemo(() => {
    const par = new Map<string, Map<string, string>>();
    for (const d of rhDetenues || []) {
      const cle = String(d.orgId || '');
      if (!cle) continue;
      if (!par.has(cle)) par.set(cle, new Map());
      par.get(cle)!.set(String(d.code), String(d.niveau || ''));
    }
    return par;
  }, [rhDetenues]);

  /** Ce que le dossier de la personne permet de conclure pour ce poste.
   *  `null` = le poste n'exige rien, il n'y a rien à conclure. */
  const deduit = (orgId: string, codePoste: string): 'habilite' | 'formation' | 'non' | null => {
    /* L'exigence s'écrit de DEUX façons dans le référentiel — une chaîne
       (`'AUT-INDIKO-1'`) ou un objet (`{ code, niveauMin }`). Les deux formes
       coexistent en base ; n'en lire qu'une viderait la moitié des postes de
       leurs exigences, donc conclurait « rien à déduire » à tort. */
    const exigences = (rhPostes.find((p) => p.code === codePoste)?.habilitationsRequises || [])
      .map((e) => (typeof e === 'string' ? e : String(e?.code || ''))).filter(Boolean);
    if (!exigences.length) return null;
    const siennes = detenues.get(orgId) || new Map();
    const tenues = exigences.filter((c) => siennes.has(c));
    if (!tenues.length) return 'non';
    // « formation » est un ÉTAT, pas un degré : une seule exigence tenue en
    // formation suffit à ce que la personne ne soit pas encore habilitée.
    if (tenues.length < exigences.length) return 'formation';
    return [...tenues].some((c) => siennes.get(c) === 'formation') ? 'formation' : 'habilite';
  };

  // Une matrice de 18 personnes × 7 disciplines se lit en diagonale. Les deux
  // questions qu'on lui pose vraiment sont « qui est en formation ? » et
  // « qui n'est habilité nulle part ? » — d'où des pastilles, pas seulement une
  // recherche par nom. Le filtre par discipline réduit les colonnes : régler
  // les habilitations d'une paillasse ne demande pas de voir les six autres.
  const [q, setQ] = useState('');
  const [filtre, setFiltre] = useState('tous');
  const [disc, setDisc] = useState('');

  const etats = (id: string) => toutesDiscs.map((d) => hab[id]?.[d]).filter(Boolean) as string[];
  const enFormation = tousUsers.filter((u) => etats(u.id).includes('formation'));
  const sansAucune = tousUsers.filter((u) => etats(u.id).length === 0);

  const qq = q.trim().toLowerCase();
  const users = (filtre === 'formation' ? enFormation : filtre === 'aucune' ? sansAucune : tousUsers)
    .filter((u) => !qq || `${u.nom} ${u.fonction}`.toLowerCase().includes(qq));
  const discs = disc ? toutesDiscs.filter((d) => d === disc) : toutesDiscs;
  const ETIQ = { habilite: 'Habilité', formation: 'En formation', vide: 'Non habilité' } as const;

  /* ── LA MATRICE NE MODIFIE PLUS RIEN (décision d'Inès, 25/08/2026) ────────
   *
   * Elle faisait tourner l'état d'un clic — Habilité → En formation → Non
   * habilité — et écrivait `ged-setting.habilitations`, un JSON de réglage.
   * Trois défauts, dont le troisième est le vrai :
   *
   *  1. `void saveSettings(...)` : la promesse était jetée. Aucune attente,
   *     aucun sablier, aucun retour en cas d'échec — l'écran ne savait pas si
   *     l'écriture avait eu lieu (constaté en production).
   *  2. Chaque clic réécrivait la matrice ENTIÈRE (18 personnes × N postes) :
   *     de là la lenteur, et deux clics rapprochés s'écrasaient l'un l'autre.
   *  3. Surtout : une habilitation posée ici n'a NI dossier, NI grille, NI
   *     avis, NI décision datée — alors que c'est ce JSON que lit le verrou de
   *     signature. On pouvait donc habiliter quelqu'un d'un clic, sans trace
   *     opposable. Une habilitation se prononce dans un dossier, jamais dans
   *     une case de tableau.
   *
   * Cliquer OUVRE désormais le dossier de la personne. La matrice reste ce
   * qu'elle doit être : une vue d'ensemble.
   */
  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <H1 eyebrow={<>Compétences · <RefISO code="6.2" /><Norme art="6.2" /></>} title="Compétences — qui tient quel poste" />
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.55 }}>
        Une colonne par <strong>poste de travail</strong> : c’est ce qu’on occupe un jour donné, ce que le
        planning affecte, et ce que la grille d’habilitation décrit. C’est aussi ce que le moteur lit
        pour autoriser une signature.
        <br />
        Cette vue ne se modifie pas : cliquer une case ouvre le <strong>dossier
        d’habilitation</strong> de la personne, où l’accord se prononce sur pièces
        — grille, avis du tuteur, décision datée. Une case ne prouve rien.
      </p>
      {!!heritages.length && (
        <p style={{ fontSize: 12.5, color: 'var(--amber-700)', margin: 0, lineHeight: 1.55 }}>
          {heritages.length} colonne(s) surmontée(s) d’un liseré ambre ne correspondent à aucun poste en service
          ({heritages.join(' · ')}). Ce sont des habilitations héritées : elles restent affichées parce
          qu’elles restent en base et opposables en audit, mais aucun planning ne les emploie.
        </p>
      )}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 260px', maxWidth: 420 }}>
          <FilterBar
            q={q} onQ={setQ} placeholder="Rechercher un opérateur…"
            chips={[
              { k: 'tous', label: 'Tous', n: tousUsers.length },
              { k: 'formation', label: 'En formation', n: enFormation.length },
              { k: 'aucune', label: 'Aucune habilitation', n: sansAucune.length },
            ]}
            actif={filtre} onChip={setFiltre}
            total={tousUsers.length} affiches={users.length}
            onReset={() => { setQ(''); setFiltre('tous'); setDisc(''); }} />
        </div>
        <label style={{ display: 'grid', gap: 4, marginBottom: 14 }}>
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>Poste</span>
          <Selecteur value={disc} onChange={setDisc} aria-label="Limiter à un poste de travail"
            style={{ ...inputStyle, height: 38, width: 'auto', minWidth: 220 }}>
            <option value="">Tous les postes</option>
            <optgroup label="Postes de travail">
              {codesPostes.map((c) => <option key={c} value={c}>{libellePoste(c)}</option>)}
            </optgroup>
            {/* Les héritages ne sont pas mêlés aux postes en service : on doit
                voir d'un coup d'œil que ces colonnes-là n'ouvrent plus. */}
            {!!heritages.length && (
              <optgroup label="Axes hérités — plus aucun poste actif">
                {heritages.map((h) => <option key={h} value={h}>{h}</option>)}
              </optgroup>
            )}
          </Selecteur>
        </label>
      </div>

      <div style={{ ...card, overflow: 'auto' }}>
        {attend && <Skeleton rows={5} height={38} />}
        {!attend && !users.length && (
          <EmptyState
            titre="Aucun opérateur ne correspond à ces critères."
            indice={filtre === 'aucune' ? 'Personne n’est dépourvu d’habilitation — c’est le résultat attendu sur un effectif à jour.' : 'Élargissez la recherche ou revenez à « Tous ».'} />
        )}
        {!attend && !!users.length && (
        <table className="ged-tableau" ref={enTeteCollant} style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
          <thead>
            <tr>
              {/* ⚠️ LA COLONNE « OPÉRATEUR » EST FIGÉE À GAUCHE (relevé du
                  27/08/2026, P1). La matrice fait 2 416 px pour 984 visibles :
                  60 % est hors écran, et il faut défiler horizontalement pour
                  lire les postes. Sans cette colonne figée, on lit une ligne de
                  croix et de tirets sans savoir DE QUI il s'agit — la donnée
                  est là, elle ne veut plus rien dire.
                  `zIndex: 4` : elle croise l'en-tête collant (3) au coin haut
                  gauche, et c'est elle qui doit passer devant. Fond OPAQUE,
                  sinon les colonnes défilent au travers. */}
              <th scope="col" style={{ textAlign: 'start', padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', background: 'var(--gray-25)', borderBottom: '1px solid var(--border-default)', fontSize: 'var(--tableau-entete-compacte-police)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', position: 'sticky', insetInlineStart: 0, zIndex: 4 }}>Opérateur</th>
              {/* Les colonnes HÉRITÉES portent une teinte propre : elles ne
                  correspondent à aucun poste en service, et les confondre avec
                  les postes ouverts ferait croire qu'on peut encore y affecter
                  quelqu'un. */}
              {discs.map((d) => (
                <th scope="col" key={d}
                  title={estProcessus(d)
                    ? `« ${d} » ne correspond à aucun poste en service — habilitation héritée, conservée parce qu'elle reste opposable en audit.`
                    : `${libellePoste(d)} (${d})`}
                  /* ⚠️ LE REPÈRE ANNONCÉ DOIT SE VOIR (relevé DAT-13 du
                     26/08/2026 : « le message annonce 6 colonnes en ambre alors
                     qu'aucune colonne n'est ambre à l'écran »).
                     Le fond `--amber-50` est un blanc cassé : posé à côté de
                     `--gray-25`, l'écart est imperceptible sur un écran de
                     paillasse. Une phrase qui désigne un repère invisible fait
                     douter de la phrase, puis de l'écran.
                     Le liseré supérieur, lui, se voit : il marque la COLONNE,
                     ce que le message annonce, et non la seule cellule d'en-tête. */
                  style={{ padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', background: estProcessus(d) ? 'var(--amber-50)' : 'var(--gray-25)', borderTop: estProcessus(d) ? '3px solid var(--amber-500)' : '3px solid transparent', borderBottom: '1px solid var(--border-default)', fontSize: 'var(--tableau-entete-compacte-police)', textTransform: 'uppercase', letterSpacing: '0.03em', color: estProcessus(d) ? 'var(--amber-700)' : 'var(--text-muted)' }}>
                  {estProcessus(d) ? d : libellePoste(d)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                {/* Le nom suit le défilement horizontal — voir l'en-tête. Le
                    fond reprend celui de la carte : transparent, les croix des
                    colonnes passeraient dessous. */}
                <td style={{ padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', borderTop: '1px solid var(--border-subtle)', fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', position: 'sticky', insetInlineStart: 0, zIndex: 2, background: 'var(--surface-card)' }}>{u.nom}</td>
                {discs.map((d) => {
                  const v = hab[u.id]?.[d];
                  return (
                    <td key={d} style={{ padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', borderTop: '1px solid var(--border-subtle)', textAlign: 'center' }}>
                      <>
                          {/* Cliquer OUVRE LE DOSSIER, ne modifie rien : une
                              habilitation se prononce sur pièces (grille, avis,
                              décision datée), jamais dans une case. */}
                          {/* La clé `selectedPersonne` existe depuis le
                              25/08/2026 : le clic ouvre le dossier DE CETTE
                              PERSONNE, et non plus l'écran où il fallait la
                              retrouver à la main. */}
                          <button onClick={() => naviguerVers('parcours', { cle: 'selectedPersonne', valeur: u.id })}
                            title={`${u.nom} · ${libellePoste(d)} — ${ETIQ[v || 'vide']}. Ouvrir son dossier d’habilitation pour le modifier.`}
                            style={{ fontFamily: 'var(--font-sans)', fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '4px 9px', cursor: 'pointer', border: 'none', background: v === 'habilite' ? 'var(--green-50)' : v === 'formation' ? 'var(--amber-50)' : 'var(--gray-25)', color: v === 'habilite' ? 'var(--green-600)' : v === 'formation' ? 'var(--amber-600)' : 'var(--text-faint)', minWidth: 70 }}>
                            {v === 'habilite' ? 'Habilité' : v === 'formation' ? 'Formation' : '—'}
                          </button>
                          {/* L'ÉCART avec ce que dit le dossier de la personne.
                              Il ne s'affiche QUE lorsqu'il y a un écart : une
                              annotation sur chaque case ferait un mur de texte
                              où l'on ne verrait plus les quelques cas à
                              regarder. */}
                          {(() => {
                            const dd = deduit(u.id, d);
                            if (dd === null) return null;
                            const memeChose = (dd === 'non' && !v) || dd === v;
                            if (memeChose) return null;
                            const dit = dd === 'habilite' ? 'habilité' : dd === 'formation' ? 'en cours' : 'aucune exigence tenue';
                            return (
                              <span
                                title={`Le dossier de ${u.nom} dit « ${dit} » pour ce poste, d’après les habilitations qu’elle ou il détient. La case, elle, dit « ${ETIQ[v || 'vide']} ». Rien n’est modifié automatiquement — l’écart est signalé, c’est à vous de trancher.`}
                                style={{ display: 'block', fontSize: 9.5, fontWeight: 700, color: 'var(--cyan-700)', cursor: 'help', marginTop: 2 }}>
                                dossier : {dit}
                              </span>
                            );
                          })()}
                        </>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>
    </div>
  );
}
